import React, { useState } from "react";
import { Modal, Button, Form, Alert, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EyeOff } from "../../../assets/images/eyeOff-icon.svg";
import { ReactComponent as Eye } from "../../../assets/images/eye-icon.svg";
import { useSelector, useDispatch } from "react-redux";
import {
  forgotPassword,
  verifyOtp,
  resetPassword,
} from "../../../redux/slices/authSlice";
import { FiX } from "react-icons/fi";

const OtpModal = ({ show, handleClose }) => {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [invalidIndexes, setInvalidIndexes] = useState([]);
  const [step, setStep] = useState("email");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const dispatch = useDispatch();
  const [emailInput, setEmailInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const authStore = useSelector((state) => state.auth);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();

  const resetModalState = () => {
    setOtp(["", "", "", ""]);
    setError("");
    setSuccess("");
    setInvalidIndexes([]);
    setStep("email");
    setPassword("");
    setConfirmPassword("");
    setShowPass(false);
    setShowConfirmPass(false);
    setIsLoading(false);
  };

  const handleChange = (value, index) => {
    const skippedIndexes = otp
      .slice(0, index)
      .map((v, i) => (v === "" ? i : null))
      .filter((i) => i !== null);

    if (skippedIndexes.length > 0) {
      setError("Please fill previous boxes first.");
      setInvalidIndexes(skippedIndexes);
      return;
    }

    if (/^[0-9]$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setError("");
      setInvalidIndexes([]);
      if (index < 3) {
        document.getElementById(`otp-${index + 1}`).focus();
      }
    } else if (value === "") {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
      setInvalidIndexes([]);
    }
    if (e.key === "Enter") {
      handleVerify();
    }
  };



  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!emailInput.trim()) {
      setError("Email is required");
      return;
    }


    try {
      await dispatch(forgotPassword(emailInput)).unwrap();

      setOtp(["", "", "", ""]);
      setStep("otp");
      setResendSuccess(false);
    } catch (error) {
      console.error("Forgot password failed:", error);
      setError("Something went wrong. Please try again.");
    }
  };

  const handleResend = async (e) => {
    if (e) e.preventDefault();


    setIsResending(true);
    setResendSuccess(false);

    try {
      const result = await dispatch(forgotPassword(emailInput)).unwrap();

      if (result?.data?.success || result?.success) {
        setOtp(["", "", "", ""]);
        setResendSuccess(true);

        setTimeout(() => setResendSuccess(false), 3000);
      }
    } catch (error) {
      console.error("Resend OTP failed:", error);
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const handleVerify = () => {
    const email = authStore?.email || emailInput;
    if (!email) {
      setError("Email not found. Please try again.");
      return;
    }

    const enteredOtp = otp.join("");
    const emptyIndexes = otp
      .map((v, i) => (v === "" ? i : null))
      .filter((i) => i !== null);

    if (emptyIndexes.length > 0) {
      setError("Please fill all OTP boxes.");
      setInvalidIndexes(emptyIndexes);
      return;
    }

    const payload = {
      email,
      otp: enteredOtp,
    };


    dispatch(verifyOtp(payload))
      .unwrap()
      .then((res) => {
        setStep("reset");
        setError("");
      })
      .catch((err) => {
        setError(err?.data?.message || "OTP does not match. Please try again.");
        setInvalidIndexes([]);
      });
  };

  const handlePaste = (e) => e.preventDefault();

  const handleSave = () => {
    const email = authStore?.email || emailInput;
    const enteredPassword = password;
    const enteredConfirmPassword = confirmPassword;

    setError("");
    setSuccess("");

    if (!enteredPassword || !enteredConfirmPassword) {
      setError("Both password fields are required.");
      return;
    }

    if (enteredPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (enteredPassword !== enteredConfirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const payload = {
      email,
      password: enteredPassword,
      confirm_password: enteredConfirmPassword,
    };


    dispatch(resetPassword(payload))
      .unwrap()
      .then(() => {
        setSuccess("Your password has been changed successfully.");
        setPassword("");
        setConfirmPassword("");

        setTimeout(() => {
          resetModalState();
          handleClose();
          navigate("/login");
        }, 2000);
      })
      .catch((err) => {
        console.error("Password reset failed:", err);
        setError("Failed to reset password. Please try again.");
      });
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        handleClose();
        resetModalState();
      }}
      centered
      className="otp-modal custom-modal"
      size="md"
    >
      {step === "email" && (
        <>
          <Modal.Header>
            <h5 className="modal-title">Forgot Your Password?</h5>
            <Button
              className="close-btn"
              onClick={() => {
                handleClose();
                resetModalState();
              }}
            >
              <FiX size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body className="pt-0">
            <p className="pb-3">
              Enter your email, and we'll send an OTP to reset your password.
            </p>
            <Form>
              <Form.Group className="form-group">
                <Form.Label className="col-label">Email address</Form.Label>
                <Form.Control
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Enter email address"
                  required
                  className="col-input"
                />
              </Form.Group>
              <div className="buttons">
                <Button
                  type="submit"
                  className="btn btn-primary w-100"
                  onClick={handleSubmit}
                >
                  Send OTP
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </>
      )}
      {step === "otp" && (
        <>
          <Modal.Header>
            <h5 className="modal-title">Reset Your Password</h5>
            <Button
              className="close-btn"
              onClick={() => {
                handleClose();
                resetModalState();
              }}
            >
              <FiX size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body className="pt-0">
            <p className="pb-3">
              We've sent a One-Time Password (OTP) to your registered email
              address. Please enter the code below to verify your identity and
              reset your password .
            </p>

            <div className="otp-inputs">
              {otp &&
                otp?.map((val, index) => (
                  <Form.Control
                    key={index}
                    type="text"
                    maxLength={1}
                    value={val}
                    id={`otp-${index}`}
                    onChange={(e) => handleChange(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    className={`text-center ${invalidIndexes.includes(index) ? "invalid-box" : ""
                      }`}
                    placeholder="_"
                  />
                ))}
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
            <div className="buttons pt-4">
              <Button
                type="button"
                className="btn btn-primary w-100"
                onClick={handleVerify}
                disabled={isLoading}
              >
                Verify
              </Button>
            </div>

            <p className="receive-otp text-center pt-3">
              Didn’t receive OTP?
              <span
                className="resend-link"
                role="button"
                tabIndex={0}
                onClick={handleResend}
                style={{
                  pointerEvents: isResending ? "none" : "auto",
                  opacity: isResending ? 0.6 : 1,
                  marginLeft: "6px",
                }}
              >
                {isResending ? "Resending..." : "Resend"}
              </span>
            </p>
            {resendSuccess && (
              <p className="text-success mt-2" style={{ fontSize: "0.9rem" }}>
                OTP successfully sent.
              </p>
            )}
          </Modal.Body>
        </>
      )}

      {step === "reset" && (
        <>
          <Modal.Header>
            <h5 className="modal-title">Reset Password</h5>
            <Button
              className="close-btn"
              onClick={() => {
                handleClose();
                resetModalState();
              }}
            >
              <FiX size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body className="pt-0">
            <p className="pb-3">Please fill the form to reset your password</p>
            <Form>
              <Form.Group className="form-group">
                <Form.Label className="col-label">Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPass ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="col-input"
                  />
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    aria-label="Toggle password visibility"
                  >
                    {showPass ? <Eye /> : <EyeOff />}
                  </Button>
                </InputGroup>
              </Form.Group>
              <Form.Group className="form-group">
                <Form.Label className="col-label">Confirm Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showConfirmPass ? "text" : "password"}
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="col-input"
                  />
                  <Button
                    variant="outline-secondary"
                    type="button"
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                    aria-label="Toggle password visibility"
                  >
                    {showConfirmPass ? <Eye /> : <EyeOff />}
                  </Button>
                </InputGroup>
              </Form.Group>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <div className="buttons pt-4">
                <Button className="btn btn-primary w-100" onClick={handleSave}>
                  Save
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </>
      )}
    </Modal>
  );
};

export default OtpModal;
